/**
 * Download action button for the file-preview dialog header.
 *
 * Resolves the source's download URL on click, builds an `<a download>`
 * anchor on the fly and triggers it. Cross-origin URLs may ignore the
 * `download` attribute (browser respects the response `Content-
 * Disposition` instead) — that's acceptable: the file still leaves the
 * browser, just possibly opens in a tab.
 *
 * Doesn't render itself when the source can't be downloaded — chrome
 * decides which actions to mount based on `source.capabilities`.
 */
import { Download } from "lucide-react";
import { toast } from "sonner";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { FileSource } from "../types";

export function DownloadAction({ source }: { source: FileSource }) {
  if (!source.capabilities.canDownload) return null;

  const handleDownload = async () => {
    try {
      const url = source.resolveDownloadUrl
        ? await source.resolveDownloadUrl()
        : null;
      if (!url) {
        toast.error("No download URL available");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = source.filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloading ${source.filename}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleDownload}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
          aria-label={`Download ${source.filename}`}
        >
          <Download className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Download file</p>
      </TooltipContent>
    </Tooltip>
  );
}
